import {DateTime} from "luxon";
import {parseApiInstant} from "core/format/dateFormat";
import ExpiresAfter from "../domain/ExpiresAfter";
import {
	allowedFiniteExpiryValues, canUseCapability, expiryLifetimeMinutes, Plan,
} from "../home/gating";
import {normalizeEmails} from "../home/components/createFormLogic";
import {TransferLegalHoldState} from "../domain/TransferLegalHoldState";
import {TransferRecoveryState} from "../domain/TransferRecoveryState";
import type {PatchTransferBody} from "./contacts/TransferApi";

/**
 * Post-send transfer-mutation RULES (#93 extend expiry, #94 edit, #96 recover expired) — react-free by mandate
 * (`logic-outside-react`): eligibility, the extend-choice ladder, the expiry-date math and the
 * add/revoke recipient diff all live here so they are unit-testable without rendering and survive a UI
 * rewrite. The expiry ladder helpers are REUSED from `home/gating` (never duplicated), and recipient
 * normalization is REUSED from `createFormLogic` (the one canonical mailbox canonicalizer). The
 * `useTransferMutations` hook and the dialogs only bind these; they hold no rules. The api is the final
 * authority (it re-validates window/cap/entitlement) — these mirror it for a correct, quiet UI.
 */

/** Whether the plan may extend a transfer's expiry (#93) — PLUS and up; FREE/BASIC see the upsell. */
export function canExtendExpiry(plan: Plan): boolean {
	return canUseCapability(plan, "extendExpiry");
}

/**
 * Whether the plan may edit a sent transfer (#94: message/recipients; kitecorp/transferify-api-service#220:
 * add/remove files) — PLUS and up; FREE/BASIC see the upsell.
 */
export function canEditAfterSending(plan: Plan): boolean {
	return canUseCapability(plan, "editAfterSending");
}

/**
 * Whether a transfer's expiry can be extended at all: it must be a hosted, FINITE retention that is NOT
 * under legal hold. A null/NEVER expiry (instant-P2P links, or a transfer with no TTL) has no total to
 * extend, and a held transfer has no expiry to move — the api rejects both — so the "Extend expiry"
 * action is hidden for them. Sub-day retentions are finite and therefore extendable
 * (kitecorp/transferify#90); {@link extendChoices} offers the longer sub-day rungs before the day ones.
 *
 * <p>The hold clause is not cosmetic: the api extends by RE-TAGGING every object, and the tag it would
 * overwrite is the one that pins a held transfer's bytes. It answers a 409 rather than doing that, and
 * offering a control that can only 409 is worse than not offering it.
 */
export function isExtendable(transfer: {expireAfter: ExpiresAfter | null; legalHold?: boolean}): boolean {
	return transfer.legalHold !== true
		&& transfer.expireAfter !== null && transfer.expireAfter !== ExpiresAfter.NEVER;
}

/**
 * The retentions offered as extension targets: the plan-allowed FINITE rungs STRICTLY longer than the
 * current one. New total retention is measured from creation (the api requires strictly longer and inside
 * the plan window), so this filters {@link allowedFiniteExpiryValues} — already the plan window — down to
 * values whose LIFETIME exceeds the current expiry's. Empty when the transfer is already at the plan's
 * longest rung.
 *
 * Compared in minutes, not days (kitecorp/transferify#90). A day count answers `undefined` for a sub-day
 * rung, and `undefined > undefined` is false for every candidate — so a 15-minute transfer's extend sheet
 * would have opened with NO choices at all rather than failing anywhere a reader could see it.
 */
export function extendChoices(plan: Plan, current: ExpiresAfter): ExpiresAfter[] {
	const currentMinutes = expiryLifetimeMinutes(current);
	if (currentMinutes === null) {
		return [];
	}
	return allowedFiniteExpiryValues(plan).filter(value => {
		// `null` is "indefinite", which allowedFiniteExpiryValues already excludes — narrowed rather than
		// defaulted, so the impossible case cannot quietly masquerade as a zero-length retention.
		const minutes = expiryLifetimeMinutes(value);
		return minutes !== null && minutes > currentMinutes;
	});
}

/** The subset of a transfer's summary these expiry/recovery rules read. */
export interface ExpiryFacts {
	createdDate: string;
	expireAfter: ExpiresAfter | null;
	expiresAt?: string | null;
	recoveryState?: TransferRecoveryState;
	recoverableUntil?: string | null;
	/**
	 * Whether the owner has placed the transfer under legal hold (kitecorp/transferify#85). Load-bearing
	 * for {@link expiresAtOf}, not decoration: a held transfer is served with `expiresAt: null` while
	 * KEEPING its `expireAfter`, so the created+retention fallback would otherwise resurrect the very
	 * expiry the hold suspends and report a preserved transfer as expired.
	 */
	legalHold?: boolean;
}

/**
 * Parses one of the api's offset-free `LocalDateTime` strings, which it means as UTC. Exported because
 * callers that want a SPECIFIC served instant (e.g. `recoverableUntil`) must use this rather than
 * {@link expiresAtOf} with a spoofed field: expiresAtOf falls through to created+retention arithmetic
 * when its input is null, which would render the LINK expiry as if it were the recovery deadline.
 */
export function parseUtc(value: string | null | undefined): Date | null {
	return parseApiInstant(value)?.toJSDate() ?? null;
}

/**
 * The instant a transfer's link stops working, or null when it has no TTL. Prefers the SERVED
 * `expiresAt` and only falls back to `createdDate + retention` arithmetic when the field is missing
 * (a stale cached payload or a fixture): once a transfer has been RECOVERED its link outlives that
 * arithmetic by the recovery window, so recomputing it client-side would under-report the expiry
 * (kitecorp/transferify-api-service#96).
 */
export function expiresAtOf(transfer: ExpiryFacts): Date | null {
	const served = parseUtc(transfer.expiresAt);
	if (served !== null) {
		return served;
	}
	// A held transfer keeps its `expireAfter` and is served with a null `expiresAt` — the two facts the
	// fallback below would combine into the expiry the hold exists to suspend (kitecorp/transferify#85).
	// Checked before the fallback, not inside it, because "no expiry" is the ANSWER here, not a gap.
	if (transfer.legalHold === true) {
		return null;
	}
	if (transfer.expireAfter === null) {
		return null;
	}
	// Minutes, not days: a sub-day rung has no day count (kitecorp/transferify#90), and the null this
	// returns for the indefinite rung is the same null the explicit NEVER test used to produce.
	const lifetimeMinutes = expiryLifetimeMinutes(transfer.expireAfter);
	if (lifetimeMinutes === null) {
		return null;
	}
	const created = DateTime.fromISO(transfer.createdDate, {zone: "utc"});
	if (!created.isValid) {
		return null;
	}
	return created.plus({minutes: lifetimeMinutes}).toJSDate();
}

/**
 * The link expiry a given extension target would produce, on the api's own rules: the retention is
 * measured from CREATION, and a spent recovery keeps its offset (a RECOVERED transfer's link already
 * sits `recoveryDays` past created+retention, and extending moves it to created+target+recoveryDays).
 *
 * The offset is derived from the SERVED expiry rather than a recovery-window field, so the ui never
 * re-implements the server's arithmetic — it just carries whatever gap the server is already
 * reporting. Null when the target or the current retention is not FINITE — i.e. absent or indefinite.
 */
export function extendedExpiryOf(transfer: ExpiryFacts, target: ExpiresAfter): Date | null {
	if (transfer.expireAfter === null) {
		return null;
	}
	// Both ends measured in minutes (kitecorp/transferify#90) so a sub-day current retention or target is
	// dated exactly rather than rounded to the 1-day storage class its bytes ride.
	const currentMinutes = expiryLifetimeMinutes(transfer.expireAfter);
	const targetMinutes = expiryLifetimeMinutes(target);
	if (currentMinutes === null || targetMinutes === null) {
		return null;
	}
	const created = DateTime.fromISO(transfer.createdDate, {zone: "utc"});
	if (!created.isValid) {
		return null;
	}
	const plainExpiry = created.plus({minutes: currentMinutes});
	const served = parseUtc(transfer.expiresAt);
	const recoveryOffsetMs = served === null ? 0 : served.getTime() - plainExpiry.toMillis();
	return new Date(created.plus({minutes: targetMinutes}).toMillis() + recoveryOffsetMs);
}

/** Whether the plan may recover expired transfers (#96, PREMIUM only). */
export function canRecoverExpiredTransfers(plan: Plan): boolean {
	return canUseCapability(plan, "recoverExpiredTransfers");
}

/** Whether the plan may place a legal hold (kitecorp/transferify#85, PREMIUM only). */
export function canPlaceLegalHold(plan: Plan): boolean {
	return canUseCapability(plan, "legalHold");
}

/**
 * What the row's legal-hold control should be, mirroring the api's own two-verb sub-resource.
 *
 * <p>"release" is deliberately NOT plan-gated, exactly as the api leaves the DELETE ungated: a
 * downgraded owner must always be able to stop pinning bytes they are still billed for, so the control
 * that lifts a hold has to outlive the entitlement that placed it. Offering "place" is gated, because
 * that one costs storage.
 *
 * <p>Unlike the recovery action this DOES have a locked/upsell variant, and the difference is real: a
 * hold depends on nothing that was snapshotted at creation, so an upgrade genuinely makes every
 * existing eligible transfer holdable. The recovery upsell was removed because it could not be honest;
 * this one can.
 */
export type LegalHoldAction = "place" | "release" | "locked" | "hidden";

export function legalHoldActionOf(plan: Plan, transfer: LegalHoldFacts): LegalHoldAction {
	// The api's OWN verdict, never a client-side mirror of its guards. One of its inputs — whether this
	// row's storage backend can move an already-written object's purge date — is invisible here, and on
	// both live environments it is false for every finite-retention transfer. A ui that re-derived only
	// the shape guards would offer PREMIUM the action on those and be refused every time.
	switch (transfer.legalHoldState) {
		case TransferLegalHoldState.HELD:
			return "release";
		case TransferLegalHoldState.AVAILABLE:
			return canPlaceLegalHold(plan) ? "place" : "locked";
		default:
			// UNSUPPORTED / INCOMPLETE / EXPIRED: no plan unlocks any of them, so there is no honest
			// upsell to show — the same rule the recovery action follows.
			return "hidden";
	}
}

/** The subset of a transfer's summary {@link legalHoldActionOf} reads. */
export interface LegalHoldFacts {
	legalHoldState: TransferLegalHoldState;
}

/** Whether the transfer's link has already lapsed at `now` (a never-expiring transfer never has). */
export function isExpired(transfer: ExpiryFacts, now: Date): boolean {
	const expiresAt = expiresAtOf(transfer);
	return expiresAt !== null && expiresAt.getTime() <= now.getTime();
}

/**
 * What the row's Recover control should be (#96) — and there is deliberately NO locked/upsell variant.
 *
 * `recovery_days` is a CREATION-time snapshot, so every transfer a Free/Basic/Plus user ever created
 * carries 0: upgrading to Premium cannot make any of them recoverable, it only changes what FUTURE
 * uploads retain. A locked "Recover — upgrade to unlock" item on such a row would sell a recovery we
 * can never perform, and would then silently vanish the moment the user paid (the api answers
 * INELIGIBLE for every one of their existing rows). What those rows DO get is the honest terminal
 * state from {@link recoveryStatusOf}.
 *
 * So the action is shown only when the api itself says RECOVERABLE, which by construction implies the
 * bytes are still retained AND the owner's tier sells recovery.
 *
 * <p>What stays banned is the ACTION, not every mention of Premium on the row. Two hints sit beside it,
 * split by whether the bytes are still there: {@link showsPurgedRecoveryHint} (ui#1666) speaks about what
 * FUTURE uploads retain on a row whose bytes are gone, and {@link showsRecoverableUpgradeHint} (ui#1787)
 * offers to recover THIS transfer on a row whose window is still open. The second is not a contradiction
 * of the paragraph above: the reason the ACTION has no locked variant is that a lower tier's rows were
 * created without a window at all, while these rows were created WITH one and only the entitlement is
 * missing — so upgrading really does bring them back, and the api's own guard order confirms it.
 */
export type RecoveryAction = "available" | "hidden";

export function recoveryActionOf(plan: Plan, transfer: ExpiryFacts, now: Date): RecoveryAction {
	if (!isExpired(transfer, now) || !canRecoverExpiredTransfers(plan)) {
		return "hidden";
	}
	return transfer.recoveryState === TransferRecoveryState.RECOVERABLE ? "available" : "hidden";
}

/**
 * The row's honest expiry/recovery status at `now`, or null while the transfer is simply live. Kept
 * separate from {@link recoveryActionOf} on purpose: an expired transfer whose bytes are gone still
 * owes the owner a truthful line ("files deleted") even though no action can be offered.
 *
 * "recoverable" is an OFFER, so it is plan-gated by the same rule that removed the locked menu item —
 * a plan that cannot recover must not be told a transfer is recoverable (the api gates on the CURRENT
 * tier, so a lapsed subscription is refused even though the bytes are still retained). "recovered",
 * "purged" and "expired" are FACTS about the transfer and stay plan-independent.
 *
 * <p>The downgrade to "expired" is deliberately not the whole answer for such a row, which is why
 * "expired" here does NOT mean "nothing more to say": {@link showsRecoverableUpgradeHint} reads the same
 * facts and puts the upgrade that unlocks it beside the status. A status line cannot carry that, because
 * "recoverable for N days" would read as something the reader can act on today, and they cannot.
 */
export type RecoveryStatus = "recoverable" | "recovered" | "purged" | "expired";

export function recoveryStatusOf(plan: Plan, transfer: ExpiryFacts, now: Date): RecoveryStatus | null {
	if (transfer.recoveryState === TransferRecoveryState.RECOVERED) {
		return "recovered";
	}
	if (!isExpired(transfer, now)) {
		return null;
	}
	if (transfer.recoveryState === TransferRecoveryState.RECOVERABLE) {
		return canRecoverExpiredTransfers(plan) ? "recoverable" : "expired";
	}
	// PURGED means we DID keep the bytes and the window closed; INELIGIBLE means we never kept them.
	// Only the former earns the "files deleted" wording — the latter is a plain expiry.
	return transfer.recoveryState === TransferRecoveryState.PURGED ? "purged" : "expired";
}

/**
 * Whether a purged row should carry the quiet Premium recovery hint (ui#1666).
 *
 * The hint is PREVENTION, not rescue: its copy says what Premium keeps for future uploads, because a
 * purged transfer's bytes are already gone and no upgrade brings them back. That is what lets it sit
 * on a row where {@link recoveryActionOf} refuses to offer anything.
 *
 * Scoped to "purged" alone. "expired" is what a below-Premium plan sees for a transfer that never
 * carried a window AND for one whose bytes are still retained, so hanging the hint there would put a
 * sentence about a closed recovery window on rows that never had one. "purged" is the single status
 * that means we really did keep the bytes and the window ran out, which is exactly the disappointment
 * the copy answers. The still-retained half of that "expired" bucket has its own hint in
 * {@link showsRecoverableUpgradeHint}, and the two never fire on the same row.
 *
 * Premium is excluded because it already has the recovery being advertised. Everyone else sees it,
 * including the lapsed Premium owner who is by far the likeliest reader: a purged row can only exist
 * on an account that HAD a recovery window when the transfer was created.
 */
export function showsPurgedRecoveryHint(plan: Plan, transfer: ExpiryFacts, now: Date): boolean {
	return recoveryStatusOf(plan, transfer, now) === "purged" && !canRecoverExpiredTransfers(plan);
}

/**
 * Whether an expired row whose recovery window is STILL OPEN should offer the upgrade that would
 * recover it (ui#1787) — the sibling of {@link showsPurgedRecoveryHint} for the opposite case.
 *
 * This one is RESCUE and can say so. `TransferMutationService.recover` checks entitlement → shape →
 * liveness → already-spent → window, in that order, so for a row that is RECOVERABLE inside its window
 * the bytes are still on disk and the CURRENT tier is the only thing missing. Re-subscribing genuinely
 * brings back this exact transfer, which is what makes "recover this transfer" true here and nowhere
 * else. The javadoc on the api's `recoverExpiredTransfers` says an upgrade unlocks the action "for
 * FUTURE transfers only"; that sentence is about objects lifecycle already PURGED and does not describe
 * an open window.
 *
 * The audience is small and self-selecting by construction: `recovery_days` is a creation-time snapshot,
 * so a row like this can only belong to an account that HAD recovery when it was uploaded — a lapsed or
 * downgraded Premium owner. Premium itself is excluded, since {@link recoveryActionOf} already offers
 * them the real thing.
 *
 * The DEADLINE decides, not the state word. The api leaves `recoveryState` at RECOVERABLE until its
 * purge sweep runs, so a row read minutes after its window closed still carries that state; offering an
 * upgrade there would sell a recovery the api would refuse on its last guard.
 */
export function showsRecoverableUpgradeHint(plan: Plan, transfer: ExpiryFacts, now: Date): boolean {
	if (canRecoverExpiredTransfers(plan) || !isExpired(transfer, now)) {
		return false;
	}
	if (transfer.recoveryState !== TransferRecoveryState.RECOVERABLE) {
		return false;
	}
	// recoveryDaysLeft answers null for a row that kept no window and 0 once the deadline passed, so one
	// comparison covers both "never had one" and "too late" without re-parsing the instant here.
	const daysLeft = recoveryDaysLeft(transfer, now);
	return daysLeft !== null && daysLeft > 0;
}

/**
 * Whole days left before the recovery window closes, rounded UP so a partial day still reads as a day
 * the owner can act on ("2 days left" until the final 24h, never a premature "0"). Null when the
 * transfer keeps no window; 0 once the deadline has passed.
 */
export function recoveryDaysLeft(transfer: ExpiryFacts, now: Date): number | null {
	const deadline = parseUtc(transfer.recoverableUntil);
	if (deadline === null) {
		return null;
	}
	const remainingMs = deadline.getTime() - now.getTime();
	return remainingMs <= 0 ? 0 : Math.ceil(remainingMs / 86_400_000);
}

/** The add/revoke split between the current recipients and an edited list (both canonicalized). */
export interface RecipientDiff {
	addRecipients: string[];
	revokeRecipients: string[];
}

/**
 * Diffs an edited recipient list against the original: mailboxes present only in the edited list are
 * ADDED, mailboxes present only in the original are REVOKED. Both sides are canonicalized (trim,
 * lower-case, de-dupe, drop invalid) through the shared {@link normalizeEmails}, so casing or a
 * trailing comma never spuriously registers as a change.
 */
export function recipientDiff(original: readonly string[], edited: readonly string[]): RecipientDiff {
	const originalSet = new Set(normalizeEmails(original));
	const editedList = normalizeEmails(edited);
	const editedSet = new Set(editedList);
	return {
		addRecipients: editedList.filter(email => !originalSet.has(email)),
		revokeRecipients: [...originalSet].filter(email => !editedSet.has(email)),
	};
}

/**
 * Builds the #94 edit PATCH body from a transfer's original state and the dialog's edited state,
 * including ONLY the fields that actually changed (absent = unchanged). A message equal to the original
 * (treating a null original as "") is omitted; a blank edited message that differs is sent as `""` to
 * clear it. Add/revoke lists are omitted when empty.
 */
export function buildEditPatch(params: {
	originalMessage: string | null;
	message: string;
	originalRecipients: readonly string[];
	recipients: readonly string[];
}): PatchTransferBody {
	const patch: PatchTransferBody = {};
	if (params.message !== (params.originalMessage ?? "")) {
		patch.message = params.message;
	}
	const {addRecipients, revokeRecipients} = recipientDiff(params.originalRecipients, params.recipients);
	if (addRecipients.length > 0) {
		patch.addRecipients = addRecipients;
	}
	if (revokeRecipients.length > 0) {
		patch.revokeRecipients = revokeRecipients;
	}
	return patch;
}

/** Whether a PATCH body carries any change — false disables Save (nothing to submit). */
export function hasChanges(patch: PatchTransferBody): boolean {
	return Object.keys(patch).length > 0;
}
